import type { JSONContent } from "@tiptap/react";
import { documentJsonToMarkdown } from "@workith/editor-schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRef } from "./collaborationEditorState";
import type { CollaborationCloseClassification } from "./collaborationProvider";
import type { RejectedChangeSnapshot } from "./sectionedCollaborationProvider";

/**
 * Current-tab recovery for content the server refused (R12-R13, R18).
 *
 * Everything here reads what this tab already holds in memory. A revoked
 * document must never trigger another fetch - the credential is gone, the
 * request would be refused, and a refusal after denial reads as a retry of the
 * rejected work - so an unloaded section contributes nothing and is disclosed
 * as an omission instead.
 *
 * The Markdown lives in a ref for the lifetime of one offer and is wiped on
 * export, on dismiss and on any authoritative reload. It is never written to
 * storage, never logged and never sent anywhere.
 */

/** What the banner may show. Never the Markdown itself. */
export interface CollaborationRecoverySummary {
  readonly scope: RejectedChangeSnapshot["scope"];
  readonly classification: CollaborationCloseClassification;
  /** Rooms whose already-loaded content is in the export. */
  readonly includedCount: number;
  /** Sections this tab never loaded, so they are absent from the export. */
  readonly omittedCount: number;
  /** Whether the export is less than the whole document. */
  readonly isPartial: boolean;
  readonly filename: string;
}

export interface CollaborationRecoveryPayload extends CollaborationRecoverySummary {
  readonly markdown: string;
  readonly capturedAt: number;
}

/** Content of one already-loaded room, or `null` when this tab never loaded it. */
export type RecoveryContentReader = (sectionId: string | null) => JSONContent | null;

export interface BuildCollaborationRecoveryInput {
  documentId: string;
  snapshot: RejectedChangeSnapshot;
  /** Manifest order known to this tab. Empty for a legacy single-room document. */
  sectionIds?: readonly string[];
  /** Whether the root room carries text (legacy) or only the manifest (sectioned). */
  hasRootContent: boolean;
  readContent: RecoveryContentReader;
  now?: () => number;
}

const MAX_FILENAME_STEM = 80;
const UNSAFE_FILENAME_CHARS = /[^a-zA-Z0-9._-]+/g;

/**
 * A filename that cannot escape the download directory or shadow a dotfile.
 * Anything outside the safe set collapses to `-`, so a document id or section id
 * that ever carries a path separator, a quote or a control character cannot
 * reach the file system.
 */
export function safeRecoveryFilename(parts: readonly string[], capturedAt: number): string {
  const stem = parts
    .map((part) => part.replace(UNSAFE_FILENAME_CHARS, "-").replace(/^[.-]+/, ""))
    .filter(Boolean)
    .join("-")
    .slice(0, MAX_FILENAME_STEM);
  const timestamp = new Date(capturedAt).toISOString().replace(/[:.]/g, "-");
  return `workith-recovery-${stem || "document"}-${timestamp}.md`;
}

/**
 * Which rooms this export may read, in the order they appear in the document.
 *
 * A room-local failure is only ever about its own section, so siblings that are
 * still editable are left out of it entirely. A document-wide denial walks the
 * manifest, then appends any captured room the manifest no longer references -
 * that room's text is in this tab and dropping it would lose work.
 */
function recoveryCandidates(
  snapshot: RejectedChangeSnapshot,
  sectionIds: readonly string[],
  hasRootContent: boolean
): (string | null)[] {
  if (snapshot.scope === "section") {
    return snapshot.rooms.map((room) => room.sectionId);
  }

  const candidates: (string | null)[] = [];
  if (hasRootContent && snapshot.rooms.some((room) => room.sectionId === null)) {
    candidates.push(null);
  }
  candidates.push(...sectionIds);
  for (const room of snapshot.rooms) {
    if (room.sectionId !== null && !candidates.includes(room.sectionId)) {
      candidates.push(room.sectionId);
    }
  }
  return candidates;
}

/**
 * Serializes what this tab still holds into one Markdown payload.
 *
 * Returns `null` when there is nothing to recover: no room had refused local
 * changes, or everything that is loaded is empty. An empty clean document must
 * not produce a recovery offer.
 */
export function buildCollaborationRecoveryPayload({
  documentId,
  snapshot,
  sectionIds = [],
  hasRootContent,
  readContent,
  now = Date.now,
}: BuildCollaborationRecoveryInput): CollaborationRecoveryPayload | null {
  if (!snapshot.rooms.some((room) => room.hasPendingChanges)) return null;

  const documents: string[] = [];
  let includedCount = 0;
  let omittedCount = 0;

  for (const sectionId of recoveryCandidates(snapshot, sectionIds, hasRootContent)) {
    const json = readContent(sectionId);
    if (!json) {
      omittedCount += 1;
      continue;
    }
    includedCount += 1;
    documents.push(documentJsonToMarkdown(json).trim());
  }

  const markdown = documents.filter(Boolean).join("\n\n");
  if (markdown === "") return null;

  const capturedAt = snapshot.capturedAt || now();
  const sectionScope = snapshot.scope === "section" ? snapshot.rooms[0]?.sectionId : undefined;

  return {
    scope: snapshot.scope,
    classification: snapshot.classification,
    capturedAt,
    markdown: `${markdown}\n`,
    includedCount,
    omittedCount,
    isPartial: omittedCount > 0,
    filename: safeRecoveryFilename(
      sectionScope ? [documentId, "section", sectionScope] : [documentId],
      capturedAt
    ),
  };
}

/** Where an export ends up. Both leave a copy Workith can no longer delete. */
export type CollaborationRecoveryStatus =
  | "ready"
  | "copying"
  | "downloading"
  | "copied"
  | "downloaded"
  | "copyFailed"
  | "downloadFailed";

export interface RecoveryExportDependencies {
  copyText?: (text: string) => Promise<boolean>;
  saveFile?: (filename: string, text: string) => boolean;
}

/**
 * Clipboard write with the one fallback that still works without the async
 * Clipboard API (older Safari, and any non-secure context). Failure is reported,
 * never swallowed: the user has to know the text is still only in this tab.
 */
export async function copyRecoveryMarkdown(text: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Falls through to the execCommand path below.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand?.("copy") ?? false;
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

/** Saves the payload as a file through a same-tab object URL, revoked at once. */
export function saveRecoveryMarkdown(filename: string, text: string): boolean {
  try {
    const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

export interface UseCollaborationRecoveryOptions
  extends Omit<BuildCollaborationRecoveryInput, "snapshot" | "sectionIds">,
    RecoveryExportDependencies {
  /** The latest capture, or `null` once the document reloads authoritatively. */
  snapshot: RejectedChangeSnapshot | null;
  /** Manifest order, read at capture time so no section is fetched afterwards. */
  readSectionIds?: () => readonly string[];
  /**
   * Changes when a different set of rooms becomes readable in this tab.
   *
   * A capture can land in the same commit that mounts the editors it has to read
   * from, and an editor that is not mounted yet reads as "never loaded". This
   * lets the capture be retried while nothing has been produced yet - it never
   * causes a fetch, because every read stays local.
   */
  contentKey?: string;
  /** Lets the owner release the capture it is holding once this offer is over. */
  onResolved?: () => void;
}

export interface CollaborationRecovery {
  /** Non-null exactly while the banner has something to offer. */
  readonly summary: CollaborationRecoverySummary | null;
  readonly status: CollaborationRecoveryStatus;
  copy(): void;
  download(): void;
  dismiss(): void;
}

/**
 * Owns one recovery offer: capture, export, wipe.
 *
 * The capture runs in an effect, so it sees the editors as they are - still
 * mounted, still holding the refused text, because a terminal close leaves the
 * Y.Doc and the editor alone. The Markdown is held in a ref and dropped as soon
 * as the offer is resolved; the summary outlives it only so the confirmation
 * stays on screen after the content is gone.
 */
export function useCollaborationRecovery({
  documentId,
  snapshot,
  hasRootContent,
  readContent,
  readSectionIds,
  contentKey,
  onResolved,
  copyText = copyRecoveryMarkdown,
  saveFile = saveRecoveryMarkdown,
}: UseCollaborationRecoveryOptions): CollaborationRecovery {
  const markdownRef = useRef<string | null>(null);
  const [summary, setSummary] = useState<CollaborationRecoverySummary | null>(null);
  const [status, setStatus] = useState<CollaborationRecoveryStatus>("ready");
  /** The capture this offer belongs to, so one capture is never built twice. */
  const capturedRef = useRef<RejectedChangeSnapshot | null>(null);
  /**
   * Whether this offer already ended in a successful export. The owner releases
   * the capture right after, and that release must not erase the confirmation
   * the user is reading.
   */
  const exportedRef = useRef(false);
  /**
   * Whether this capture already produced its offer - or had it exported or
   * dismissed. Either way the editors are not read again for it.
   */
  const offeredRef = useRef(false);
  const documentIdRef = useRef(documentId);
  const readContentRef = useLatestRef(readContent);
  const readSectionIdsRef = useLatestRef(readSectionIds);
  const onResolvedRef = useLatestRef(onResolved);

  // biome-ignore lint/correctness/useExhaustiveDependencies: contentKey is the retry trigger
  useEffect(() => {
    const isReload = documentIdRef.current !== documentId;
    documentIdRef.current = documentId;

    if (snapshot === null) {
      // An authoritative reload, or the owner releasing what this hook exported.
      // The payload never survives either; the confirmation survives only the
      // second one.
      capturedRef.current = null;
      markdownRef.current = null;
      offeredRef.current = false;
      if (isReload || !exportedRef.current) {
        exportedRef.current = false;
        setSummary(null);
        setStatus("ready");
      }
      return;
    }
    if (capturedRef.current !== snapshot) {
      capturedRef.current = snapshot;
      exportedRef.current = false;
      offeredRef.current = false;
    } else if (offeredRef.current) {
      // Already offered, exported or dismissed. Re-reading the editors now would
      // resurrect a payload the user is done with.
      return;
    }

    const payload = buildCollaborationRecoveryPayload({
      documentId,
      snapshot,
      sectionIds: readSectionIdsRef.current?.() ?? [],
      hasRootContent,
      readContent: readContentRef.current,
    });
    if (!payload) {
      // Nothing was refused, or nothing readable has content: no offer yet. A
      // later `contentKey` - an editor that finished mounting - retries it.
      markdownRef.current = null;
      setSummary(null);
      setStatus("ready");
      return;
    }

    const { markdown, capturedAt: _capturedAt, ...rest } = payload;
    markdownRef.current = markdown;
    offeredRef.current = true;
    setSummary(rest);
    setStatus("ready");
  }, [documentId, snapshot, hasRootContent, contentKey, readContentRef, readSectionIdsRef]);

  /** Drops the payload; the summary stays so the outcome remains readable. */
  const wipe = useCallback(() => {
    markdownRef.current = null;
    exportedRef.current = true;
    onResolvedRef.current?.();
  }, [onResolvedRef]);

  const copy = useCallback(() => {
    const markdown = markdownRef.current;
    if (!markdown) return;
    setStatus("copying");
    void copyText(markdown).then((ok) => {
      setStatus(ok ? "copied" : "copyFailed");
      if (ok) wipe();
    });
  }, [copyText, wipe]);

  const download = useCallback(() => {
    const markdown = markdownRef.current;
    if (!markdown || !summary) return;
    setStatus("downloading");
    const ok = saveFile(summary.filename, markdown);
    setStatus(ok ? "downloaded" : "downloadFailed");
    if (ok) wipe();
  }, [saveFile, summary, wipe]);

  const dismiss = useCallback(() => {
    markdownRef.current = null;
    exportedRef.current = false;
    offeredRef.current = true;
    setSummary(null);
    setStatus("ready");
    onResolvedRef.current?.();
  }, [onResolvedRef]);

  return { summary, status, copy, download, dismiss };
}
